'use client';

import { useEffect } from 'react';
import { getLenis } from '@/lib/lenisInstance';
import { HIJACK } from '@/lib/motionTokens';

// quart out — longer slow tail than the cubic. 1 - (1-t)^4 puts the
// deceleration over a wider range so the chapter lands without the
// "punch" the cubic gave at the end of the snap.
const easeOutQuart = (t: number): number => 1 - Math.pow(1 - t, 4);

// Scroll hijack for the InfoSection. Mirrors the CameraRig pattern:
// one wheel/touch/keyboard gesture snaps to the next chapter station via
// `lenis.scrollTo` with lock. The user never scrubs through the section
// content — they jump, the blob travels, then text staggers in once the
// station has arrived (handled inside ChapterStation via useInView).
//
// Why offsetTop is read on every event (not cached):
//   The journey's ScrollTrigger pin adds a pin-spacer that pushes the
//   InfoSection's offsetTop down by the pin distance (~14*vh). That happens
//   AFTER React mounts — sometimes after this hook's RAF poll starts. Caching
//   section.offsetTop once gives stale stations whose range overlaps the
//   journey, which would make this hook preventDefault the journey's wheel
//   events and break the camera-rig snap. Reading offsetTop each event is
//   cheap and immune to pin/refresh timing.
//
// Boundaries:
//   • At station 0 + scroll up → release so Lenis can carry the user back
//     into the journey (which has its own hijack handling the return).
//   • At the last station + scroll down → release so the user can scroll
//     naturally into whatever sits below the InfoSection (footer, page bottom).

// All sensitivity knobs (travel duration, inertia tail, touch threshold,
// easing) live in `lib/motionTokens.ts` under `HIJACK.info`. Tune them
// there if the snap pacing needs to change.
const { travelSeconds, inertiaTailMs, touchMinDeltaPx, revealLockMs } = HIJACK.info;

export type InfoHijackOptions = {
  stationCount: number;
  sectionSelector: string;
  /** Skip installing handlers entirely — used on mobile (low tier) where
   * native scroll + iOS momentum should run unobstructed. */
  enabled?: boolean;
};

export function useInfoHijack({ stationCount, sectionSelector, enabled = true }: InfoHijackOptions): void {
  useEffect(() => {
    if (!enabled) return;
    let currentIdx = 0;
    let animating = false;
    let lastStepAt = 0;
    // Locks input after a snap settles ON A REAL CHAPTER STATION so the
    // ChapterStation reveal animation (headline wipe + line cascade) finishes
    // before another wheel/touch/key can advance. Transition anchor (idx=-1)
    // bypasses this so wheel-up back into the journey stays responsive.
    let revealLockUntil = 0;
    let registered = false;
    let raf = 0;

    const getSection = (): HTMLElement | null =>
      document.querySelector(sectionSelector);

    const getStations = (sec: HTMLElement): number[] => {
      const top = sec.offsetTop;
      const vh = window.innerHeight;
      return Array.from({ length: stationCount }, (_, i) => top + i * vh);
    };

    const isInsideSection = (sec: HTMLElement, y: number): boolean => {
      const top = sec.offsetTop;
      const bottom = top + sec.offsetHeight;
      // Lower bound extends exactly one viewport below info.offsetTop so the
      // pin-release transition window (~100vh of journey-canvas exit scroll)
      // is owned by us. The 2px tolerance absorbs floating-point overshoots
      // from Lenis arriving a hair shy of trigger.end. Pushing the buffer
      // wider would overlap the journey's last segment and steal wheels
      // before the camera has even reached station 8.
      const transitionBuffer = window.innerHeight;
      return y >= top - transitionBuffer - 2 && y <= bottom - window.innerHeight + 2;
    };

    const nearestIndex = (stations: number[], y: number): number => {
      // Below station 0 (transition zone): return -1 so a `dir=1` step lands
      // exactly on station 0 (chapter 1) and a `dir=-1` step bails out to
      // release the wheel back to the journey.
      if (stations.length === 0) return 0;
      if (y < stations[0] - 0.5) return -1;
      let bestI = 0;
      let bestD = Infinity;
      for (let i = 0; i < stations.length; i++) {
        const d = Math.abs(stations[i] - y);
        if (d < bestD) {
          bestD = d;
          bestI = i;
        }
      }
      return bestI;
    };

    const goToStep = (
      sec: HTMLElement,
      stations: number[],
      dir: 1 | -1,
    ): boolean => {
      // currentIdx can be -1 (transition zone, just below station 0). Stepping
      // outside the [0, length-1] range is mapped to the transition anchor —
      // sec.offsetTop - vh, which sits exactly at the journey's trigger.end —
      // so wheel up at chapter 1 snaps the user back into journey territory
      // instead of doing nothing. Stepping above the last station is rejected
      // (the boundary release in onWheel runs before us in that case).
      const rawNext = currentIdx + dir;
      let nextIdx: number;
      let target: number;
      if (rawNext < 0) {
        nextIdx = -1;
        target = sec.offsetTop - window.innerHeight;
      } else if (rawNext > stations.length - 1) {
        return false;
      } else {
        nextIdx = rawNext;
        target = stations[nextIdx];
      }
      if (nextIdx === currentIdx) return false;
      currentIdx = nextIdx;
      animating = true;
      const lenis = getLenis();
      const isStation = nextIdx >= 0;
      const finish = () => {
        animating = false;
        lastStepAt = performance.now();
        if (isStation) revealLockUntil = performance.now() + revealLockMs;
      };
      if (lenis) {
        lenis.scrollTo(target, {
          duration: travelSeconds,
          easing: easeOutQuart,
          lock: true,
          onComplete: finish,
        });
      } else {
        window.scrollTo({ top: target, behavior: 'smooth' });
        window.setTimeout(finish, travelSeconds * 1000);
      }
      return true;
    };

    const onWheel = (e: WheelEvent) => {
      const sec = getSection();
      if (!sec) return;
      const y = window.scrollY;
      if (!isInsideSection(sec, y)) {
        if (!animating) currentIdx = nearestIndex(getStations(sec), y);
        return;
      }
      if (animating) {
        e.preventDefault();
        return;
      }
      // Reveal lock — text has to finish drawing before the user can move on.
      if (performance.now() < revealLockUntil) {
        e.preventDefault();
        return;
      }
      if (performance.now() - lastStepAt < inertiaTailMs) {
        e.preventDefault();
        return;
      }
      const stations = getStations(sec);
      // Resync from scrollY each event so the hijack stays correct after
      // resizes or any other scroll that bypassed goToStep.
      if (!animating) currentIdx = nearestIndex(stations, y);
      const dir: 1 | -1 = e.deltaY > 0 ? 1 : -1;
      // Boundary releases — ONLY when already past the boundary:
      //   • currentIdx === -1 (transition zone) + dir = -1 → release; Lenis
      //     carries the user up into the journey (CameraRig takes over).
      //   • last station + dir = 1 → release down to the page bottom.
      // From station 0 + dir = -1 we DON'T release — goToStep snaps to the
      // transition anchor so wheel-up reads as island-to-island in both
      // directions.
      if (dir === -1 && currentIdx <= -1) return;
      if (dir === 1 && currentIdx >= stations.length - 1) return;
      e.preventDefault();
      goToStep(sec, stations, dir);
    };

    let touchStartY = 0;
    const onTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY;
    };
    const onTouchMove = (e: TouchEvent) => {
      const sec = getSection();
      if (!sec) return;
      if (!isInsideSection(sec, window.scrollY)) return;
      if (animating) e.preventDefault();
    };
    const onTouchEnd = (e: TouchEvent) => {
      const sec = getSection();
      if (!sec) return;
      const y = window.scrollY;
      if (!isInsideSection(sec, y) || animating) return;
      if (performance.now() < revealLockUntil) return;
      const dy = touchStartY - e.changedTouches[0].clientY;
      if (Math.abs(dy) < touchMinDeltaPx) return;
      const stations = getStations(sec);
      currentIdx = nearestIndex(stations, y);
      const dir: 1 | -1 = dy > 0 ? 1 : -1;
      if (dir === -1 && currentIdx <= -1) return;
      if (dir === 1 && currentIdx >= stations.length - 1) return;
      goToStep(sec, stations, dir);
    };

    const onKey = (e: KeyboardEvent) => {
      // WCAG 2.1.2 — never trap keys when a focusable element owns the
      // keyboard. A user tabbing through the footer / contact CTA inside
      // the info section needs Space and Arrow to behave normally on the
      // focused link/button; the hijack only owns input when nothing
      // interactive is focused (i.e. body or null).
      const active = document.activeElement;
      const owned =
        active !== null &&
        active !== document.body &&
        active !== document.documentElement;
      if (owned) return;

      const sec = getSection();
      if (!sec) return;
      const y = window.scrollY;
      if (!isInsideSection(sec, y) || animating) return;
      if (performance.now() < revealLockUntil) {
        e.preventDefault();
        return;
      }
      let dir: 1 | -1 | 0 = 0;
      if (
        e.key === 'ArrowDown' ||
        e.key === 'ArrowRight' ||
        e.key === 'PageDown' ||
        e.key === ' '
      ) {
        dir = 1;
      }
      if (e.key === 'ArrowUp' || e.key === 'ArrowLeft' || e.key === 'PageUp') {
        dir = -1;
      }
      if (dir === 0) return;
      const stations = getStations(sec);
      currentIdx = nearestIndex(stations, y);
      if (dir === -1 && currentIdx <= -1) return;
      if (dir === 1 && currentIdx >= stations.length - 1) return;
      e.preventDefault();
      goToStep(sec, stations, dir);
    };

    // Wait for the InfoSection to exist in the DOM before installing the
    // listeners. We do NOT wait for offsetTop > 0 here — the handlers read
    // offsetTop fresh each event so they tolerate pin/refresh changes.
    let attempts = 0;
    const tryStart = () => {
      attempts++;
      const sec = getSection();
      if (sec) {
        const stations = getStations(sec);
        currentIdx = nearestIndex(stations, window.scrollY);
        window.addEventListener('wheel', onWheel, { passive: false });
        window.addEventListener('touchstart', onTouchStart, { passive: true });
        window.addEventListener('touchmove', onTouchMove, { passive: false });
        window.addEventListener('touchend', onTouchEnd, { passive: true });
        window.addEventListener('keydown', onKey);
        registered = true;
        return;
      }
      if (attempts > 600) return;
      raf = requestAnimationFrame(tryStart);
    };
    tryStart();

    return () => {
      if (raf) cancelAnimationFrame(raf);
      if (registered) {
        window.removeEventListener('wheel', onWheel);
        window.removeEventListener('touchstart', onTouchStart);
        window.removeEventListener('touchmove', onTouchMove);
        window.removeEventListener('touchend', onTouchEnd);
        window.removeEventListener('keydown', onKey);
      }
    };
  }, [stationCount, sectionSelector, enabled]);
}
